/**
 * Product previews for the homepage, built from markup rather than screenshots.
 *
 * Two reasons not to ship an image here. A screenshot of a score is a picture of
 * a claim nobody can check, and it goes stale the first time the engine
 * changes. These render the engine's real output for the fictional sample
 * resume (see src/data/homeSample.ts, which a test keeps in step with the
 * engine), so the numbers on the homepage are the numbers the tool gives.
 * They also cost no image request on the page whose LCP matters most.
 *
 * Elevation: the design system keeps the UI flat and reserves shadow for the
 * resume page itself (docs/design.md, "Spacing, radius, elevation"). The hero
 * is the one place where a resume page and the checker sit on top of each
 * other, so it is also the one place outside the builder with a shadow.
 */

import { AlertTriangle, ArrowRight, FileCheck2, Lightbulb, Sparkles, XCircle } from 'lucide-react'
import { ScoreBar, ScoreRing, SeverityIcon } from '@/components/ui'
import { SAMPLE_ATS, SAMPLE_BULLET, SAMPLE_PERSON } from '@/data/homeSample'

const SampleBadge = () => (
  <span className="rounded-full border border-base-300 bg-base-200 px-2 py-0.5 text-[0.7rem] font-medium text-base-content/75">
    Sample
  </span>
)

/** Hero visual: the sample resume page with the ATS checker's result over it. */
export const HeroPreview = () => (
  <figure className="relative mx-auto w-full max-w-xl" aria-labelledby="hero-preview-caption">
    {/* The resume page. Hidden on the narrowest phones, where two stacked
        cards would push the first section below three screens of scroll; the
        checker card alone still shows what the product does. */}
    <div className="resume-page hidden rounded-xl p-5 pb-16 text-[0.8rem] leading-snug sm:block sm:w-[82%]" style={{ fontFamily: 'var(--font-resume-helvetica)' }}>
      <p className="text-base font-bold">{SAMPLE_PERSON.name}</p>
      <p className="text-[0.75rem] text-slate-600">
        {SAMPLE_PERSON.headline} · {SAMPLE_PERSON.location}
      </p>
      <p className="mt-3 border-b border-slate-300 pb-0.5 text-[0.65rem] font-bold tracking-wider">EXPERIENCE</p>
      <div className="mt-1.5 flex items-baseline justify-between gap-2">
        <p className="font-semibold">
          {SAMPLE_PERSON.role}, {SAMPLE_PERSON.company}
        </p>
        <p className="shrink-0 text-[0.7rem] text-slate-600">{SAMPLE_PERSON.dates}</p>
      </div>
      <ul className="mt-1 list-disc space-y-1 pl-4">
        {SAMPLE_PERSON.bullets.map((bullet, index) => (
          <li key={bullet}>
            {index === SAMPLE_PERSON.flagged ? (
              /* Marked with an underline as well as a tint, so the flagged line
                 does not depend on colour to be found. */
              <mark className="rounded-sm bg-amber-100 px-0.5 text-inherit underline decoration-amber-600 decoration-wavy underline-offset-2">
                {bullet}
              </mark>
            ) : (
              bullet
            )}
          </li>
        ))}
      </ul>
    </div>

    <div className="relative z-10 rounded-2xl border border-base-300 bg-base-100 p-4 shadow-[0_1px_2px_rgb(0_0_0/0.05),0_16px_40px_-12px_rgb(0_0_0/0.18)] sm:-mt-12 sm:ml-auto sm:w-[78%]">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-semibold">
          <FileCheck2 size={16} className="text-primary" aria-hidden="true" />
          ATS Resume Checker
        </p>
        <SampleBadge />
      </div>
      <div className="mt-3 flex items-center gap-4">
        <ScoreRing score={SAMPLE_ATS.score} size={92} label="Sample resume ATS score" sublabel="of 100" />
        <div className="min-w-0 flex-1 space-y-1.5">
          {SAMPLE_ATS.categories.map((category) => (
            <ScoreBar key={category.label} value={category.score} max={category.max} label={category.label} />
          ))}
        </div>
      </div>
      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-base-content/70">Top fixes</p>
      <ul className="mt-1.5 space-y-1.5 text-sm">
        {SAMPLE_ATS.findings.map((finding, index) => (
          <li key={finding} className="flex items-start gap-2">
            <SeverityIcon severity={index === 0 ? 'critical' : 'warning'} className="mt-0.5 shrink-0" />
            <span>{finding}</span>
          </li>
        ))}
      </ul>
    </div>

    <figcaption id="hero-preview-caption" className="mt-3 text-center text-xs text-base-content/70">
      The checker&apos;s real result for the built-in sample resume. The person is fictional.
    </figcaption>
  </figure>
)

const ISSUE_ICONS = [XCircle, AlertTriangle, AlertTriangle]

/** One bullet run through the Bullet Improver, with the engine's own wording. */
export const BulletExample = () => {
  const withMetric = SAMPLE_BULLET.suggestion.replace(/\.$/, '') + SAMPLE_BULLET.metricTemplate + '.'
  /* Split on the blanks so each one can be drawn as a visible gap. */
  const parts = withMetric.split('__')

  return (
    <div className="rounded-2xl border border-base-300 bg-base-100 p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-semibold">
          <Sparkles size={16} className="text-primary" aria-hidden="true" />
          Bullet Point Improver
        </p>
        <SampleBadge />
      </div>

      <div className="mt-4 rounded-xl border border-base-300 bg-base-200 p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-base-content/70">Your bullet</p>
          <p className="text-xs font-semibold text-error">
            {SAMPLE_BULLET.score}/100 · Needs work
          </p>
        </div>
        <p className="mt-1">{SAMPLE_BULLET.original}</p>
      </div>

      <ul className="mt-4 space-y-2 text-sm">
        {SAMPLE_BULLET.issues.map((issue, index) => {
          const Icon = ISSUE_ICONS[index]
          return (
            <li key={issue} className="flex items-start gap-2">
              <Icon size={16} className={`mt-0.5 shrink-0 ${index === 0 ? 'text-error' : 'text-warning'}`} aria-hidden="true" />
              <span>{issue}</span>
            </li>
          )
        })}
      </ul>

      <div className="mt-4 flex items-start gap-2 rounded-xl bg-primary/5 p-3 text-sm">
        <Lightbulb size={16} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
        <p>
          <span className="font-semibold">Why it matters to a reader: </span>
          {SAMPLE_BULLET.why}
        </p>
      </div>

      <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-base-content/70">Suggested rewrite</p>
      <p className="mt-1 flex items-start gap-2">
        <ArrowRight size={16} className="mt-1 shrink-0 text-success" aria-hidden="true" />
        <span>{SAMPLE_BULLET.suggestion}</span>
      </p>

      <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-base-content/70">
        Add a result (the numbers come from you)
      </p>
      <p className="mt-1">
        {parts.map((part, index) => (
          <span key={index}>
            {part}
            {index < parts.length - 1 && (
              <span className="mx-0.5 inline-block min-w-8 border-b-2 border-dashed border-primary text-center">
                <span className="sr-only">blank for your number</span>
                <span aria-hidden="true">&nbsp;</span>
              </span>
            )}
          </span>
        ))}
      </p>
    </div>
  )
}
